import { Schema } from 'yup';

import { BookkeeperConfig, BookkeeperConfigSerialized } from '@playq/services-bookkeeper';
import { SonarConfiguration, SonarConfigurationSerialized } from '@playq/services-sonar';
import { AppID, Environment } from '@playq/octopus-common';
import { CommonSuccess, GenericFailure } from '@playq/services-shared';
import { Either } from '@playq/irt';
import { Application, ApplicationSerialized } from '@playq/services-fingerprint';

import { Services2 } from '/api';
import { bookkeeperConfigSchema, fgpConfigSchema, sonarConfigSchema } from '/schemas';
import { ServicesOptions } from '/api/hooks/servicesService/types/servicesOptions';

import { createBlankFingerprintConfiguration } from './createBlankFingerprintConfiguration';
import { createBlankBookkeeperConfiguration } from './createBlankBookkeeperConfiguration';
import { createBlankSonarConfiguration } from './createBlankSonarConfiguration';

interface IServiceMethods<C, CS> {
  getClassName(): ServicesOptions;
  createBlankConfig: (env: Environment, serialized?: boolean) => C;
  serializeConfig: (cfg: C) => CS;
  deserializeConfig: (cfg: CS) => C;
  validationSchema: Schema<object>;
  getConfig$: (app: AppID, env: Environment, noCache?: boolean) => Promise<Either<GenericFailure, C>>;
  setConfig$: (app: AppID, env: Environment, cfg: C) => Promise<Either<GenericFailure, CommonSuccess>>;
}

type ServiceMethods =
  | IServiceMethods<Application, ApplicationSerialized>
  | IServiceMethods<BookkeeperConfig, BookkeeperConfigSerialized>
  | IServiceMethods<SonarConfiguration, SonarConfigurationSerialized>;

export function getServiceMethods(apiServices: Services2, selectedService: ServicesOptions): ServiceMethods {
  switch (selectedService) {
    case ServicesOptions.Fingerprint: {
      return {
        getClassName: () => ServicesOptions.Fingerprint,
        createBlankConfig: createBlankFingerprintConfiguration,
        serializeConfig: (cfg: Application) => cfg.serialize(),
        deserializeConfig: (cfg: ApplicationSerialized) => new Application(cfg),
        validationSchema: fgpConfigSchema,
        getConfig$: (app: AppID, env: Environment) =>
          apiServices.fingerprintService.get.bind(apiServices.fingerprintService)(app, env, true),
        setConfig$: (app: AppID, env: Environment, cfg: Application) =>
          apiServices.fingerprintService.upsert.bind(apiServices.fingerprintService)(app, env, cfg.integrations),
      };
    }
    case ServicesOptions.Bookkeeper: {
      return {
        getClassName: () => ServicesOptions.Bookkeeper,
        createBlankConfig: createBlankBookkeeperConfiguration,
        serializeConfig: (cfg: BookkeeperConfig) => cfg.serialize(),
        deserializeConfig: (cfg: BookkeeperConfigSerialized) => new BookkeeperConfig(cfg),
        validationSchema: bookkeeperConfigSchema,
        getConfig$: apiServices.configurationService.getBookkeeper.bind(apiServices.configurationService),
        setConfig$: apiServices.configurationService.setupBookkeeper.bind(apiServices.configurationService),
      };
    }
    case ServicesOptions.Sonar: {
      return {
        getClassName: () => ServicesOptions.Sonar,
        createBlankConfig: createBlankSonarConfiguration,
        serializeConfig: (cfg: SonarConfiguration) => cfg.serialize(),
        deserializeConfig: (cfg: SonarConfigurationSerialized) => new SonarConfiguration(cfg),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        validationSchema: sonarConfigSchema,
        getConfig$: apiServices.configurationService.getSonar.bind(apiServices.configurationService),
        setConfig$: apiServices.configurationService.setupSonar.bind(apiServices.configurationService),
      };
    }
  }
}
